/**
 * @file useMiniRouter.ts
 * @author ickeep
 * @description 路由相关的自定义 Hooks
 * @since 0.0.1
 */

import { useCallback, useContext } from 'react';

import { MiniRouterContext } from '../context/MiniRouterContext';

// 导入类型定义（从 @yimoka/react 包）
type NavigateOptions = {
  replace?: boolean;
  state?: unknown;
  preventScrollReset?: boolean;
  relative?: 'route' | 'path';
  unstable_flushSync?: boolean;
  unstable_viewTransition?: boolean;
};

type IRoutePath = {
  pathname: string;
  search: string;
  hash: string;
};

type NavigateFunction = {
  (to: string | Partial<IRoutePath>, options?: NavigateOptions): Promise<void>;
  (delta: number): Promise<void>;
};

/**
 * Location Hook
 * @summary 获取当前 location 对象
 * @returns {Object} location 对象，包含 pathname、search、hash、state、key
 * @throws {Error} 当在 MiniRouter 上下文外使用时抛出错误
 * @example
 * ```tsx
 * const location = useLocation();
 * console.log(location.pathname);
 * ```
 */
export const useLocation = () => {
  const context = useContext(MiniRouterContext);

  if (!context) {
    throw new Error('useLocation 必须在 MiniRouter 组件内使用');
  }

  return context.location;
};

/**
 * 路由参数 Hook
 * @summary 获取当前路由的参数
 * @returns {Record<string, string | undefined>} 路由参数对象
 * @throws {Error} 当在 MiniRouter 上下文外使用时抛出错误
 * @example
 * ```tsx
 * const params = useRouteParams();
 * console.log(params.id);
 * ```
 */
export const useRouteParams = () => {
  const context = useContext(MiniRouterContext);

  if (!context) {
    throw new Error('useRouteParams 必须在 MiniRouter 组件内使用');
  }

  return context.routeParams;
};

/**
 * 导航 Hook（增强版）
 * @summary 提供编程式导航方法，支持多种导航方式
 * @returns {NavigateFunction} 导航函数，支持字符串路径、IRoutePath 对象、数字（前进/后退）
 * @throws {Error} 当在 MiniRouter 上下文外使用时抛出错误
 * @example
 * ```tsx
 * const navigate = useMiniNavigate();
 * // 字符串路径
 * await navigate('/about');
 * // 带选项
 * await navigate('/about', { replace: true, state: { from: 'home' } });
 * // IRoutePath 对象
 * await navigate({ pathname: '/user', search: '?id=123', hash: '#section' });
 * // 前进/后退
 * await navigate(1); // 前进
 * await navigate(-1); // 后退
 * ```
 */
export const useMiniNavigate = (): NavigateFunction => {
  const context = useContext(MiniRouterContext);

  if (!context) {
    throw new Error('useMiniNavigate 必须在 MiniRouter 组件内使用');
  }

  const { navigate, updateLocation, location } = context;

  return useCallback<NavigateFunction>(
    // eslint-disable-next-line complexity
    (to: string | Partial<IRoutePath> | number, options?: NavigateOptions) => new Promise<void>((resolve) => {
      // 如果是数字，表示前进/后退
      if (typeof to === 'number') {
        window.history.go(to);
        // 使用 setTimeout 确保历史记录变化后再 resolve
        setTimeout(() => {
          resolve();
        }, 0);
        return;
      }

      // 构建目标 URL
      let targetUrl: string;

      if (typeof to === 'string') {
        // 字符串路径
        targetUrl = to;
      } else {
        // IRoutePath 对象
        const { pathname = location.pathname, search = '', hash = '' } = to;
        targetUrl = `${pathname}${search}${hash}`;
      }

      // 处理相对路径
      if (options?.relative && typeof to === 'string' && !to.startsWith('/')) {
        if (options.relative === 'path') {
          // 相对于当前路径
          const currentPathname = location.pathname;
          const basePath = currentPathname.endsWith('/')
            ? currentPathname.slice(0, -1)
            : currentPathname.split('/').slice(0, -1)
              .join('/') || '/';
          targetUrl = `${basePath}/${targetUrl}`;
        } else if (options.relative === 'route') {
          // 相对于当前路由（与 path 相同，因为 mini-router 不区分路由和路径）
          const currentPathname = location.pathname;
          const basePath = currentPathname.endsWith('/')
            ? currentPathname.slice(0, -1)
            : currentPathname.split('/').slice(0, -1)
              .join('/') || '/';
          targetUrl = `${basePath}/${targetUrl}`;
        }
      }

      // 构建完整 URL
      const fullUrl = new URL(targetUrl, window.location.origin);
      const fullUrlString = fullUrl.href;

      // 处理 preventScrollReset 选项
      // 如果为 true，导航后不重置滚动位置
      const shouldPreventScrollReset = options?.preventScrollReset === true;
      let scrollPosition: { x: number; y: number } | null = null;

      if (shouldPreventScrollReset && typeof window !== 'undefined') {
        // 保存当前滚动位置
        scrollPosition = {
          x: window.scrollX || window.pageXOffset || 0,
          y: window.scrollY || window.pageYOffset || 0,
        };
      }

      // 处理 unstable_flushSync 选项
      // 如果启用 flushSync，使用同步更新（通过 requestAnimationFrame 模拟）
      const shouldFlushSync = options?.unstable_flushSync === true;

      // 处理 unstable_viewTransition 选项
      // 如果浏览器支持 View Transition API，使用它
      const shouldUseViewTransition = options?.unstable_viewTransition === true
        && typeof document !== 'undefined'
        && 'startViewTransition' in document;

      // 导航函数

      const performNavigation = () => {
        // 如果提供了 state 或 replace 选项，使用 updateLocation
        if (options?.state !== undefined || options?.replace) {
          updateLocation(fullUrlString, options?.state ?? null, options?.replace ?? false);
        } else {
          // 调用原始的 navigate 函数
          navigate(targetUrl);
        }

        // 如果 preventScrollReset 为 true，恢复滚动位置
        if (shouldPreventScrollReset && scrollPosition !== null && typeof window !== 'undefined') {
          // 使用 setTimeout 确保在 DOM 更新后恢复滚动位置
          setTimeout(() => {
            if (scrollPosition) {
              window.scrollTo(scrollPosition.x, scrollPosition.y);
            }
          }, 0);
        }
      };

      // 根据选项执行导航
      if (shouldUseViewTransition && typeof document !== 'undefined' && 'startViewTransition' in document) {
        // 使用 View Transition API
        try {
          (document).startViewTransition(() => {
            performNavigation();
          });
        } catch {
          // 如果 View Transition API 不可用，回退到正常导航
          performNavigation();
        }
      } else if (shouldFlushSync) {
        // 使用 requestAnimationFrame 模拟同步更新
        requestAnimationFrame(() => {
          performNavigation();
        });
      } else {
        // 正常导航
        performNavigation();
      }

      // 使用 setTimeout 确保导航完成后再 resolve
      setTimeout(() => {
        resolve();
      }, 0);
    }),
    [navigate, updateLocation, location],
  ) as NavigateFunction;
};

/**
 * 路径 Hook
 * @summary 获取当前路径
 * @returns {string} 当前路径
 * @throws {Error} 当在 MiniRouter 上下文外使用时抛出错误
 * @example
 * ```tsx
 * const currentPath = useMiniRouterPath();
 * console.log('当前路径:', currentPath);
 * ```
 */
export const useMiniRouterPath = () => {
  const context = useContext(MiniRouterContext);

  if (!context) {
    throw new Error('useMiniRouterPath 必须在 MiniRouter 组件内使用');
  }

  return context.currentPath;
};
